/**
 * diagramState.ts — Pure, library-agnostic Diagram_State core.
 *
 * This module owns ALL diagram application logic (validation, serialization,
 * node/edge mutation, cascade deletes) for the HLD Workspace Diagram_Builder.
 * It is deliberately free of React and of any canvas library (React Flow):
 * the DiagramBuilder wrapper only maps this plain `DiagramState` JSON shape
 * to/from the library's node/edge props. Keeping the logic here makes the
 * canvas library swappable and keeps the round-trip contract (Req 11) and the
 * mutation rules (Req 10) verifiable in isolation.
 *
 * Every operation is PURE and IMMUTABLE: inputs are never mutated. On invalid
 * input an operation returns the ORIGINAL (unchanged) state together with a
 * typed error, so callers can surface an error without ever losing canvas
 * state.
 *
 * Requirements: 10.1–10.9, 11.1–11.5.
 */

import {
  DiagramState,
  DiagramNode,
  DiagramEdge,
  DiagramAnnotation,
  AnnotationKind,
  NodeType,
  EdgeProtocol,
} from '../types/hld';

// ---------------------------------------------------------------------------
// Allowed sets (Req 10.1, 10.2, 10.7)
// ---------------------------------------------------------------------------

/** The exact set of accepted Diagram_Node types (Req 10.1). */
export const NODE_TYPES: readonly NodeType[] = [
  'client',
  'service',
  'db',
  'cache',
  'queue',
  'cdn',
  'storage',
  'custom',
];

/** The exact set of accepted Diagram_Edge protocols (Req 10.7). */
export const EDGE_PROTOCOLS: readonly EdgeProtocol[] = [
  'HTTP',
  'HTTPS',
  'gRPC',
  'WebSocket',
  'TCP',
  'UDP',
];

/** The accepted Diagram_Annotation kinds (Req 10.10). */
export const ANNOTATION_KINDS: readonly AnnotationKind[] = ['group', 'divider', 'text'];

/** Diagram_Annotation label length bound after trimming. */
export const ANNOTATION_LABEL_MAX = 120;

/** Diagram_Node label length bounds after trimming (Req 10.3, 10.4). */
export const NODE_LABEL_MIN = 1;
export const NODE_LABEL_MAX = 60;

/** Diagram_Edge label length bounds after trimming (Req 10.6). */
export const EDGE_LABEL_MIN = 0;
export const EDGE_LABEL_MAX = 60;

// ---------------------------------------------------------------------------
// Error + result types
// ---------------------------------------------------------------------------

/** Stable, machine-readable codes for every failure mode. */
export type DiagramErrorCode =
  | 'INVALID_NODE_TYPE'
  | 'INVALID_NODE_LABEL'
  | 'DUPLICATE_NODE_ID'
  | 'NODE_NOT_FOUND'
  | 'EDGE_NOT_FOUND'
  | 'DUPLICATE_EDGE_ID'
  | 'MISSING_ENDPOINT'
  | 'SAME_ENDPOINT'
  | 'INVALID_EDGE_LABEL'
  | 'INVALID_EDGE_PROTOCOL'
  | 'INVALID_DIAGRAM_STATE';

/** A typed, human-readable error describing why an operation was rejected. */
export interface DiagramError {
  code: DiagramErrorCode;
  message: string;
}

/**
 * The result of a diagram mutation. On success `ok` is true and `state` is a
 * NEW state. On failure `ok` is false, `state` is the ORIGINAL unchanged state
 * (never mutated), and `error` explains the rejection.
 */
export type DiagramResult =
  | { ok: true; state: DiagramState }
  | { ok: false; state: DiagramState; error: DiagramError };

/**
 * Thrown by `deserialize` when the supplied value fails schema validation.
 * Carries the list of validation errors and NEVER carries partial state
 * (Req 11.5).
 */
export class DiagramValidationError extends Error {
  readonly code: DiagramErrorCode = 'INVALID_DIAGRAM_STATE';
  readonly errors: DiagramError[];

  constructor(errors: DiagramError[]) {
    super(
      `Invalid Diagram_State: ${errors.map((e) => e.message).join('; ') || 'unknown error'}`
    );
    this.name = 'DiagramValidationError';
    this.errors = errors;
    // Restore prototype chain for correct instanceof under ES5 target.
    Object.setPrototypeOf(this, DiagramValidationError.prototype);
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const ok = (state: DiagramState): DiagramResult => ({ ok: true, state });
const fail = (
  state: DiagramState,
  code: DiagramErrorCode,
  message: string
): DiagramResult => ({ ok: false, state, error: { code, message } });

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const isNodeType = (v: unknown): v is NodeType =>
  typeof v === 'string' && (NODE_TYPES as readonly string[]).includes(v);

const isEdgeProtocol = (v: unknown): v is EdgeProtocol =>
  typeof v === 'string' && (EDGE_PROTOCOLS as readonly string[]).includes(v);

const isAnnotationKind = (v: unknown): v is AnnotationKind =>
  typeof v === 'string' && (ANNOTATION_KINDS as readonly string[]).includes(v);

const trimmedLen = (s: string): number => s.trim().length;

/** Create an id that does not collide with any id already in `existing`. */
const uniqueId = (prefix: string, existing: Set<string>): string => {
  let id = `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  while (existing.has(id)) {
    id = `${prefix}_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  }
  return id;
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/** Build a fresh empty Diagram_State (Req 11.4). */
export const createEmptyState = (): DiagramState => ({ nodes: [], edges: [], annotations: [] });

// ---------------------------------------------------------------------------
// Validation (Req 11.5)
// ---------------------------------------------------------------------------

/**
 * Collect all schema violations for an arbitrary value. Returns an empty array
 * when the value is a fully valid `DiagramState`.
 *
 * A valid state requires: an object with `nodes` and `edges` arrays; unique,
 * non-empty node ids; each node typed from {@link NODE_TYPES} with a trimmed
 * label of 1–60 chars and a finite {x, y} position; unique, non-empty edge
 * ids; each edge referencing two DISTINCT existing node ids; optional edge
 * labels trimmed to 0–60 chars; optional edge protocols from
 * {@link EDGE_PROTOCOLS}.
 */
export function collectValidationErrors(value: unknown): DiagramError[] {
  const errors: DiagramError[] = [];

  if (!isPlainObject(value)) {
    return [{ code: 'INVALID_DIAGRAM_STATE', message: 'Diagram_State must be an object.' }];
  }

  const { nodes, edges } = value as { nodes?: unknown; edges?: unknown };

  if (!Array.isArray(nodes)) {
    errors.push({ code: 'INVALID_DIAGRAM_STATE', message: '`nodes` must be an array.' });
  }
  if (!Array.isArray(edges)) {
    errors.push({ code: 'INVALID_DIAGRAM_STATE', message: '`edges` must be an array.' });
  }
  if (errors.length > 0) {
    return errors;
  }

  const nodeArr = nodes as unknown[];
  const edgeArr = edges as unknown[];
  const nodeIds = new Set<string>();

  nodeArr.forEach((n, i) => {
    if (!isPlainObject(n)) {
      errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Node[${i}] must be an object.` });
      return;
    }
    const { id, type, label, position } = n as Record<string, unknown>;

    if (typeof id !== 'string' || id.length === 0) {
      errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Node[${i}].id must be a non-empty string.` });
    } else if (nodeIds.has(id)) {
      errors.push({ code: 'DUPLICATE_NODE_ID', message: `Duplicate node id "${id}".` });
    } else {
      nodeIds.add(id);
    }

    if (!isNodeType(type)) {
      errors.push({ code: 'INVALID_NODE_TYPE', message: `Node[${i}].type "${String(type)}" is not an allowed node type.` });
    }

    if (typeof label !== 'string' || trimmedLen(label) < NODE_LABEL_MIN || trimmedLen(label) > NODE_LABEL_MAX) {
      errors.push({ code: 'INVALID_NODE_LABEL', message: `Node[${i}].label must be ${NODE_LABEL_MIN}–${NODE_LABEL_MAX} characters after trimming.` });
    }

    if (!isPlainObject(position) || !isFiniteNumber((position as Record<string, unknown>).x) || !isFiniteNumber((position as Record<string, unknown>).y)) {
      errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Node[${i}].position must be { x: number, y: number }.` });
    }
  });

  const edgeIds = new Set<string>();

  edgeArr.forEach((e, i) => {
    if (!isPlainObject(e)) {
      errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Edge[${i}] must be an object.` });
      return;
    }
    const { id, source, target, label, protocol } = e as Record<string, unknown>;

    if (typeof id !== 'string' || id.length === 0) {
      errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Edge[${i}].id must be a non-empty string.` });
    } else if (edgeIds.has(id)) {
      errors.push({ code: 'DUPLICATE_EDGE_ID', message: `Duplicate edge id "${id}".` });
    } else {
      edgeIds.add(id);
    }

    const sourceOk = typeof source === 'string' && nodeIds.has(source);
    const targetOk = typeof target === 'string' && nodeIds.has(target);
    if (!sourceOk) {
      errors.push({ code: 'MISSING_ENDPOINT', message: `Edge[${i}].source "${String(source)}" does not reference an existing node.` });
    }
    if (!targetOk) {
      errors.push({ code: 'MISSING_ENDPOINT', message: `Edge[${i}].target "${String(target)}" does not reference an existing node.` });
    }
    if (sourceOk && targetOk && source === target) {
      errors.push({ code: 'SAME_ENDPOINT', message: `Edge[${i}] must connect two distinct nodes.` });
    }

    if (label !== undefined && (typeof label !== 'string' || trimmedLen(label) > EDGE_LABEL_MAX)) {
      errors.push({ code: 'INVALID_EDGE_LABEL', message: `Edge[${i}].label must be ${EDGE_LABEL_MIN}–${EDGE_LABEL_MAX} characters after trimming.` });
    }

    if (protocol !== undefined && !isEdgeProtocol(protocol)) {
      errors.push({ code: 'INVALID_EDGE_PROTOCOL', message: `Edge[${i}].protocol "${String(protocol)}" is not an allowed protocol.` });
    }
  });

  // Annotations are OPTIONAL and back-compatible: absent is valid. When present
  // they must be an array of well-formed group/divider/text items.
  const { annotations } = value as { annotations?: unknown };
  if (annotations !== undefined) {
    if (!Array.isArray(annotations)) {
      errors.push({ code: 'INVALID_DIAGRAM_STATE', message: '`annotations` must be an array.' });
    } else {
      annotations.forEach((a, i) => {
        if (!isPlainObject(a)) {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}] must be an object.` });
          return;
        }
        const { id, kind, position, size, label } = a as Record<string, unknown>;
        if (typeof id !== 'string' || id.length === 0) {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].id must be a non-empty string.` });
        }
        if (!isAnnotationKind(kind)) {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].kind "${String(kind)}" is not an allowed annotation kind.` });
        }
        if (!isPlainObject(position) || !isFiniteNumber((position as Record<string, unknown>).x) || !isFiniteNumber((position as Record<string, unknown>).y)) {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].position must be { x: number, y: number }.` });
        }
        if (size !== undefined) {
          const s = size as Record<string, unknown>;
          if (!isPlainObject(size) || !isFiniteNumber(s.width) || !isFiniteNumber(s.height)) {
            errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].size must be { width: number, height: number }.` });
          }
        }
        if (label !== undefined && (typeof label !== 'string' || trimmedLen(label) > ANNOTATION_LABEL_MAX)) {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].label must be at most ${ANNOTATION_LABEL_MAX} characters after trimming.` });
        }
        const { orientation, thickness, arrow } = a as Record<string, unknown>;
        if (orientation !== undefined && orientation !== 'horizontal' && orientation !== 'vertical') {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].orientation must be 'horizontal' or 'vertical'.` });
        }
        if (thickness !== undefined && thickness !== 'slim' && thickness !== 'wide') {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].thickness must be 'slim' or 'wide'.` });
        }
        if (arrow !== undefined && typeof arrow !== 'boolean') {
          errors.push({ code: 'INVALID_DIAGRAM_STATE', message: `Annotation[${i}].arrow must be a boolean.` });
        }
      });
    }
  }

  return errors;
}

/**
 * Schema-validation predicate. Returns true iff `value` is a fully valid
 * `DiagramState`. Acts as a TypeScript type guard.
 */
export function validate(value: unknown): value is DiagramState {
  return collectValidationErrors(value).length === 0;
}

// ---------------------------------------------------------------------------
// Serialization round-trip (Req 11.1–11.4)
// ---------------------------------------------------------------------------

/**
 * Produce a plain JSON `DiagramState` capturing, for each node, its id, type,
 * label and position, and for each edge, its id, source, target and direction
 * (source→target ordering) plus optional label/protocol. Returns a deep copy
 * so the caller can never mutate the source state through the result
 * (Req 11.1, 11.4).
 */
export function serialize(state: DiagramState): DiagramState {
  return {
    nodes: state.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      label: n.label,
      position: { x: n.position.x, y: n.position.y },
    })),
    edges: state.edges.map((e) => {
      const edge: DiagramEdge = { id: e.id, source: e.source, target: e.target };
      if (e.label !== undefined) edge.label = e.label;
      if (e.protocol !== undefined) edge.protocol = e.protocol;
      // Only include bidirectional if it's explicitly true to avoid schema validation issues
      if (e.bidirectional === true) edge.bidirectional = e.bidirectional;
      return edge;
    }),
    annotations: (state.annotations ?? []).map((a) => {
      const ann: DiagramAnnotation = {
        id: a.id,
        kind: a.kind,
        position: { x: a.position.x, y: a.position.y },
      };
      if (a.size) ann.size = { width: a.size.width, height: a.size.height };
      if (a.label !== undefined) ann.label = a.label;
      if (a.orientation !== undefined) ann.orientation = a.orientation;
      if (a.thickness !== undefined) ann.thickness = a.thickness;
      if (a.arrow !== undefined) ann.arrow = a.arrow;
      // Only include arrowStart/arrowEnd if they are explicitly true to avoid schema validation issues
      if (a.arrowStart === true) ann.arrowStart = a.arrowStart;
      if (a.arrowEnd === true) ann.arrowEnd = a.arrowEnd;
      return ann;
    }),
  };
}

/**
 * Parse and schema-validate a JSON value into a `DiagramState`. On invalid
 * input this THROWS a {@link DiagramValidationError} and returns NO partial
 * state, so the caller can report a load error and leave the existing canvas
 * unchanged (Req 11.2, 11.5). Accepts either a JSON string or an already
 * parsed value.
 */
export function deserialize(input: unknown): DiagramState {
  let value: unknown = input;

  if (typeof input === 'string') {
    try {
      value = JSON.parse(input);
    } catch {
      throw new DiagramValidationError([
        { code: 'INVALID_DIAGRAM_STATE', message: 'Diagram_State is not valid JSON.' },
      ]);
    }
  }

  const errors = collectValidationErrors(value);
  if (errors.length > 0) {
    throw new DiagramValidationError(errors);
  }

  // Return a normalized deep copy (equivalent to serialize of a valid state).
  return serialize(value as DiagramState);
}

/**
 * Non-throwing counterpart to {@link deserialize}. Returns a discriminated
 * result so UI code that prefers not to use try/catch can still report a load
 * error without mutating the canvas (Req 11.5).
 */
export function tryDeserialize(
  input: unknown
):
  | { ok: true; state: DiagramState }
  | { ok: false; errors: DiagramError[] } {
  try {
    return { ok: true, state: deserialize(input) };
  } catch (err) {
    if (err instanceof DiagramValidationError) {
      return { ok: false, errors: err.errors };
    }
    return {
      ok: false,
      errors: [{ code: 'INVALID_DIAGRAM_STATE', message: 'Diagram_State could not be parsed.' }],
    };
  }
}

// ---------------------------------------------------------------------------
// Node operations (Req 10.1–10.4, 10.8)
// ---------------------------------------------------------------------------

/**
 * Add a Diagram_Node. Accepts the node only if its type is exactly one of
 * {@link NODE_TYPES}; otherwise the state is unchanged and an error identifies
 * the invalid type (Req 10.1, 10.2). The node's label must also be 1–60 chars
 * after trimming (Req 10.3) and its id must be unique. Returns a new state on
 * success (Req 10.1).
 */
export function addNode(state: DiagramState, node: DiagramNode): DiagramResult {
  if (!isNodeType(node.type)) {
    return fail(
      state,
      'INVALID_NODE_TYPE',
      `Node type "${String(node.type)}" is not one of ${NODE_TYPES.join(', ')}.`
    );
  }

  if (typeof node.id !== 'string' || node.id.length === 0) {
    return fail(state, 'INVALID_DIAGRAM_STATE', 'Node id must be a non-empty string.');
  }
  if (state.nodes.some((n) => n.id === node.id)) {
    return fail(state, 'DUPLICATE_NODE_ID', `A node with id "${node.id}" already exists.`);
  }

  if (typeof node.label !== 'string' || trimmedLen(node.label) < NODE_LABEL_MIN || trimmedLen(node.label) > NODE_LABEL_MAX) {
    return fail(
      state,
      'INVALID_NODE_LABEL',
      `Node label must be ${NODE_LABEL_MIN}–${NODE_LABEL_MAX} characters after trimming.`
    );
  }

  if (!node.position || !isFiniteNumber(node.position.x) || !isFiniteNumber(node.position.y)) {
    return fail(state, 'INVALID_DIAGRAM_STATE', 'Node position must be { x: number, y: number }.');
  }

  const added: DiagramNode = {
    id: node.id,
    type: node.type,
    label: node.label,
    position: { x: node.position.x, y: node.position.y },
  };

  return ok({ ...state, nodes: [...state.nodes, added], edges: state.edges });
}

/**
 * Set the label of a Diagram_Node. Accepts a label of 1–60 characters after
 * trimming leading/trailing whitespace; otherwise rejects the label, retains
 * the previous value, and returns an error describing the constraint
 * (Req 10.3, 10.4). The stored label is the trimmed value.
 */
export function setNodeLabel(
  state: DiagramState,
  nodeId: string,
  label: string
): DiagramResult {
  const target = state.nodes.find((n) => n.id === nodeId);
  if (!target) {
    return fail(state, 'NODE_NOT_FOUND', `No node with id "${nodeId}" exists.`);
  }

  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (trimmed.length < NODE_LABEL_MIN || trimmed.length > NODE_LABEL_MAX) {
    return fail(
      state,
      'INVALID_NODE_LABEL',
      `Node label must be ${NODE_LABEL_MIN}–${NODE_LABEL_MAX} characters after trimming.`
    );
  }

  return ok({
    ...state,
    nodes: state.nodes.map((n) => (n.id === nodeId ? { ...n, label: trimmed } : n)),
    edges: state.edges,
  });
}

/**
 * Remove a Diagram_Node and cascade-remove every Diagram_Edge whose source or
 * target is that node, preserving all other nodes and non-incident edges
 * (Req 10.8). Pure and immutable; removing a non-existent node returns an
 * equivalent new state. Always succeeds.
 */
export function removeNode(state: DiagramState, nodeId: string): DiagramState {
  return {
    ...state,
    nodes: state.nodes.filter((n) => n.id !== nodeId),
    edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
  };
}

// ---------------------------------------------------------------------------
// Edge operations (Req 10.5, 10.6, 10.7, 10.9)
// ---------------------------------------------------------------------------

export interface AddEdgeOptions {
  id?: string;
  label?: string;
  protocol?: EdgeProtocol;
}

/**
 * Connect two Diagram_Nodes with a directed Diagram_Edge (source→target). An
 * edge is added if and only if BOTH endpoints exist on the canvas AND are
 * distinct; otherwise the state is left unchanged and the error identifies the
 * missing endpoint or the same-endpoint violation (Req 10.5, 10.9). An
 * optional label (0–60 trimmed, Req 10.6) and protocol
 * (one of {@link EDGE_PROTOCOLS}, Req 10.7) may be provided.
 */
export function addEdge(
  state: DiagramState,
  source: string,
  target: string,
  options: AddEdgeOptions = {}
): DiagramResult {
  const sourceExists = state.nodes.some((n) => n.id === source);
  const targetExists = state.nodes.some((n) => n.id === target);

  if (!sourceExists && !targetExists) {
    return fail(
      state,
      'MISSING_ENDPOINT',
      `Neither endpoint exists: source "${source}" and target "${target}".`
    );
  }
  if (!sourceExists) {
    return fail(state, 'MISSING_ENDPOINT', `Source node "${source}" does not exist.`);
  }
  if (!targetExists) {
    return fail(state, 'MISSING_ENDPOINT', `Target node "${target}" does not exist.`);
  }
  if (source === target) {
    return fail(state, 'SAME_ENDPOINT', 'An edge must connect two distinct nodes.');
  }

  let label: string | undefined;
  if (options.label !== undefined) {
    const trimmed = String(options.label).trim();
    if (trimmed.length > EDGE_LABEL_MAX) {
      return fail(
        state,
        'INVALID_EDGE_LABEL',
        `Edge label must be ${EDGE_LABEL_MIN}–${EDGE_LABEL_MAX} characters after trimming.`
      );
    }
    label = trimmed;
  }

  if (options.protocol !== undefined && !isEdgeProtocol(options.protocol)) {
    return fail(
      state,
      'INVALID_EDGE_PROTOCOL',
      `Edge protocol "${String(options.protocol)}" is not one of ${EDGE_PROTOCOLS.join(', ')}.`
    );
  }

  const existingEdgeIds = new Set(state.edges.map((e) => e.id));
  let id = options.id;
  if (id === undefined) {
    id = uniqueId('edge', existingEdgeIds);
  } else if (existingEdgeIds.has(id)) {
    return fail(state, 'DUPLICATE_EDGE_ID', `An edge with id "${id}" already exists.`);
  }

  const edge: DiagramEdge = { id, source, target };
  if (label !== undefined) edge.label = label;
  if (options.protocol !== undefined) edge.protocol = options.protocol;

  return ok({ ...state, nodes: state.nodes, edges: [...state.edges, edge] });
}

/**
 * Set the label of a Diagram_Edge. Accepts a label of 0–60 characters after
 * trimming (an empty label is allowed); otherwise rejects it and retains the
 * previous value (Req 10.6). The stored label is the trimmed value.
 */
export function setEdgeLabel(
  state: DiagramState,
  edgeId: string,
  label: string
): DiagramResult {
  const target = state.edges.find((e) => e.id === edgeId);
  if (!target) {
    return fail(state, 'EDGE_NOT_FOUND', `No edge with id "${edgeId}" exists.`);
  }

  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (trimmed.length < EDGE_LABEL_MIN || trimmed.length > EDGE_LABEL_MAX) {
    return fail(
      state,
      'INVALID_EDGE_LABEL',
      `Edge label must be ${EDGE_LABEL_MIN}–${EDGE_LABEL_MAX} characters after trimming.`
    );
  }

  return ok({
    ...state,
    nodes: state.nodes,
    edges: state.edges.map((e) => (e.id === edgeId ? { ...e, label: trimmed } : e)),
  });
}

/**
 * Toggle bidirectional on a Diagram_Edge. Always succeeds.
 */
export function setEdgeBidirectional(
  state: DiagramState,
  edgeId: string,
  bidirectional: boolean
): DiagramResult {
  const target = state.edges.find((e) => e.id === edgeId);
  if (!target) {
    return fail(state, 'EDGE_NOT_FOUND', `No edge with id "${edgeId}" exists.`);
  }
  return ok({
    ...state,
    edges: state.edges.map((e) => (e.id === edgeId ? { ...e, bidirectional } : e)),
  });
}

/**
 * Set the protocol of a Diagram_Edge. Accepts the protocol only if it is
 * exactly one of {@link EDGE_PROTOCOLS}; otherwise rejects it, retains the
 * previous protocol value, and returns an error (Req 10.7).
 */
export function setEdgeProtocol(
  state: DiagramState,
  edgeId: string,
  protocol: EdgeProtocol
): DiagramResult {
  const target = state.edges.find((e) => e.id === edgeId);
  if (!target) {
    return fail(state, 'EDGE_NOT_FOUND', `No edge with id "${edgeId}" exists.`);
  }

  if (!isEdgeProtocol(protocol)) {
    return fail(
      state,
      'INVALID_EDGE_PROTOCOL',
      `Edge protocol "${String(protocol)}" is not one of ${EDGE_PROTOCOLS.join(', ')}.`
    );
  }

  return ok({
    ...state,
    nodes: state.nodes,
    edges: state.edges.map((e) => (e.id === edgeId ? { ...e, protocol } : e)),
  });
}

// ---------------------------------------------------------------------------
// Annotation operations (Req 10.10) — groups / dividers / text notes
//
// Annotations are pure layout helpers, never architecture components. They live
// in a separate `annotations` array so the strict node/edge rules above are
// untouched. All ops are pure and immutable, mirroring the node/edge helpers.
// ---------------------------------------------------------------------------

/** Default box size (in canvas units) for a freshly added group / divider. */
export const DEFAULT_GROUP_SIZE = { width: 320, height: 220 };
/** Horizontal divider default: 240 long, 24 tall hit-area. */
export const DEFAULT_DIVIDER_SIZE = { width: 240, height: 24 };

/** Add a Diagram_Annotation of the given kind. Always succeeds (annotations are
 *  unconstrained layout helpers); returns a new state with the item appended. */
export function addAnnotation(
  state: DiagramState,
  annotation: DiagramAnnotation
): DiagramState {
  const existing = state.annotations ?? [];
  return { ...state, annotations: [...existing, annotation] };
}

/** Patch an annotation's position / size / label. Unknown ids are a no-op. */
export function updateAnnotation(
  state: DiagramState,
  id: string,
  patch: Partial<
    Pick<DiagramAnnotation, 'position' | 'size' | 'label' | 'orientation' | 'thickness' | 'arrow'>
  >
): DiagramState {
  const existing = state.annotations ?? [];
  return {
    ...state,
    annotations: existing.map((a) => (a.id === id ? { ...a, ...patch } : a)),
  };
}

/** Remove an annotation by id. Unknown ids are a no-op. */
export function removeAnnotation(state: DiagramState, id: string): DiagramState {
  const existing = state.annotations ?? [];
  return { ...state, annotations: existing.filter((a) => a.id !== id) };
}

/** Generate a unique annotation id not present in `existing`. */
export function makeAnnotationId(existing: DiagramAnnotation[]): string {
  return uniqueId('ann', new Set(existing.map((a) => a.id)));
}
